import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import LangDropdown from "./LangDropdown";
import "./Navbar.css";
import API from "../config";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [role, setRole]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  // ปิดเมนูมือถือเมื่อเปลี่ยนหน้า
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          localStorage.removeItem("token");
          setLoading(false);
          return;
        }

        const data = await res.json();
        setRole(data.role);
      } catch (err) {
        console.error("Failed to load profile:", err);
        localStorage.removeItem("token");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    setRole(null);
    navigate("/");
  };

  if (loading) return null;

  const isLoggedIn = !!localStorage.getItem("token");

  return (
    <header className="navbar">

      {/* LOGO */}
      <Link to="/" className="navbar-logo">
        <div className="navbar-logo-circle">
          <img src="/logo-mark.png" alt="logo" />
        </div>
        <span className="navbar-brand-name">
          Eurngkam {role === "admin" && <span className="admin-badge">Admin</span>}
        </span>
      </Link>

      {/* RIGHT ZONE: เมนู + ภาษา + avatar + hamburger */}
      <div className="navbar-right">

        {/* MENU (ลิงก์หน้า — มือถือพับเข้า drawer) */}
        <nav className={`navbar-menu ${menuOpen ? "open" : ""}`}>

          {role === "admin" && (
            <Link to="/admin" className={`nav-item ${isActive("/admin") ? "active" : ""}`}>
              {/* Grid icon */}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
              </svg>
              Dashboard
            </Link>
          )}

          <Link to="/rooms" className={`nav-item ${isActive("/rooms") ? "active" : ""}`}>
            {/* Calendar icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {t(lang, "booking")}
          </Link>

          {isLoggedIn ? (
            <>
              <Link to="/statuspage" className={`nav-item ${isActive("/statuspage") ? "active" : ""}`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {t(lang, "status")}
              </Link>

              <Link to="/profile" className={`nav-item ${isActive("/profile") ? "active" : ""}`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                {t(lang, "profile")}
              </Link>

              <LangDropdown />

              <button className="nav-item logout-btn" onClick={handleLogout}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {t(lang, "logout")}
              </button>
            </>
          ) : (
            <>
              <LangDropdown />
              <Link to="/login" className={`nav-item login-btn ${isActive("/login") ? "active" : ""}`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                {t(lang, "login")}
              </Link>
            </>
          )}
        </nav>

        {/* HAMBURGER (มือถือ) */}
        <button
          className="navbar-hamburger"
          onClick={() => setMenuOpen((p) => !p)}
          aria-label="menu"
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          )}
        </button>
      </div>
    </header>
  );
};

export default Navbar;